import {Component, effect, ElementRef, inject, input, output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {CustomInputComponent} from '../../input/custom-input/custom-input.component';
import {ImagesService} from '../../../services/general/images.service';
import {CategoryService} from '../../../services/categories/category.service';
import {ToastService} from '../../../services/general/toast.service';
import {Category} from '../../../interfaces/responses/category.interface';
import {PrimaryButtonComponent} from '../../buttons/primary-button/primary-button.component';

@Component({
  selector: 'app-add-category',
  imports: [
    CustomInputComponent,
    CommonModule,
    ReactiveFormsModule,
    PrimaryButtonComponent,
  ],
  templateUrl: './create-new-category.component.html',
  styleUrl: './create-new-category.component.scss'
})
export class CreateNewCategoryComponent {
  _images = inject(ImagesService);
  categories = [];
  @ViewChild('fileInput') fileInputRef!: ElementRef;
  _fb = inject(FormBuilder);
  categoryService = inject(CategoryService);
  isLoading: boolean = false;
  isDisabled: boolean = false;
  _toast = inject(ToastService);
  category = input<Category | null>(null);


  categoryForm: FormGroup = this._fb.group({
    name: ['', [Validators.required]],
    description: ['', [Validators.required]],
  });

  // React to changes in the 'category' input and prefill/reset the form accordingly.
  private prefillFormFromCategory = effect(() => {
    const cat = this.category(); // depend on input signal
    if (cat) {
      this.categoryForm.patchValue({
        name: cat.name ?? '',
        description: cat.description ?? '',
      });
      this.categoryForm.markAsPristine();
      this.categoryForm.markAsUntouched();
    } else {
      // reset to empty values explicitly
      this.categoryForm.reset({
        name: '',
        description: '',
      });
      this.categoryForm.markAsPristine();
      this.categoryForm.markAsUntouched();
    }
  });

  onSubmit() {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    if (this.category()) {
      this.updateCategory();
    } else {
      this.createCategory();
    }
  }

  createCategory() {
    const { name, description } = this.categoryForm.value as {
      name?: string; description?: string;
    };

    const categoryData = {
      name: name ?? '',
      description: description ?? ''
    };

    this.isLoading = true;
    this.categoryService.storeCategory(categoryData).subscribe({
      next: (response) => {
        this.loadCategoryData();
        this.categoryForm.reset();
        this._toast.success('Category created successfully');
        this.onClose();
        this.isLoading = false;
      },
      error: (error) => {
        this._toast.error('Error creating category');
        this.isLoading = false;
      }
    });
  }

  private updateCategory() {
    const cat = this.category();
    if (!cat?.id) return;

    const payload = {
      ...this.categoryForm.value,
    };

    this.isLoading = true;
    this.categoryService.updateCategory(cat.id, payload).subscribe({
      next: (response) => {
        this._toast.success('Category updated successfully');
        this.loadCategoryData();
        this.onClose();
        this.isLoading = false;
      },
      error: (error) => {
        this._toast.error('Error updating category');
        this.isLoading = false;
      }
    });
  }


  loadCategoryEvent = output<void>()
  loadCategoryData() {
    this.loadCategoryEvent.emit();
  }


  onCloseEvent = output<void>()
  onClose() {
    // Reset form fields to empty strings
    this.categoryForm.reset({
      name: '',
      description: '',
    });
    this.categoryForm.markAsPristine();
    this.categoryForm.markAsUntouched();
    this.categoryForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    this.onCloseEvent.emit();
  }
}
